import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import App from '../App';
import Login from '../features/account/Login';
import Dashboard from '../features/dashboard';
import Positions from '../features/positions/Positions';
import ChangeRequests from '../features/changeRequests';
import Users from '../features/users';
import Locations from '../features/locations';
import AddCustomAttributes from '../features/demandAttributes/Add';
import AccessDenied from '../pages/Error/AccessDenied';
import AuthorizedRouteWithLayout from './AuthorizedRouteWithLayout';
import CRPolicies from '../features/changeRequestPolicies';
import AddPosition from '../features/positions/AddAndModifyPosition/AddPosition';
import ViewPosition from '../features/positions/ViewPosition';
import AddEditUser from '../features/users/AddEditUser';
import AddEditLocation from '../features/locations/AddEditLocation';
import ViewUser from '../features/users/ViewUsers';
import BulkUploadUsers from '../features/users/BulkUploadUsers';
import ModifyPosition from '../features/positions/AddAndModifyPosition/ModifyPosition';
import Account from '../features/account/Account';
import Company from '../features/company';
import ViewCompany from '../features/company/View';
import AddCompany from '../features/company/Add';
import ModifyCRPolicies from '../features/changeRequestPolicies/ModifyCRPolicies';
import ModifyChangeRequest from '../features/changeRequests/ModifyChangeRequest';
import ForgotPassword from '../features/account/ForgotPassword';
import ResetPassword from '../features/account/ResetPassword';
import PositionsUploadPreview from '../features/positions/PositionsUploadPreview';
import BulkModifyPositions from '../features/positions/AddAndModifyPosition/BulkModifyPositions';
import ViewChangeRequests from '../features/changeRequests/ViewChangeRequest';
import ViewCRpolicies from '../features/changeRequestPolicies/ViewCRPolicies';
import ViewUpdateMappedPositions from '../features/changeRequestPolicies/ViewUpdateMappedPositions';
import ViewLocation from '../features/locations/ViewLocations';
import ViewDesignations from '../features/designations/ViewDesignations';
import AddEditDesignation from '../features/designations/AddEditDesignation';
import Designations from '../features/designations';
import Departments from '../features/departments';
import AddEditDepartment from '../features/departments/AddEditDepartment';
import ViewDepartment from '../features/departments/ViewDepartment';
import Roles from '../features/roles/Roles';
import AddEditRoles from '../features/roles/Roles/AddEditRoles';
import ChangeRequestPolicyRoles from '../features/changeRequestPolicyRoles';
import ViewRoles from '../features/roles/Roles/ViewRoles';
import UpdateIntegration from '../features/settings/UpdateIntegration';
import CompanyConfig from '../features/company/CompanyConfig';
import { RequestMaps } from '../features/settings/RequestMaps';
import { SystemPermission } from '../features/settings/SystemPermissions';
import { UpdatePositionExternalIDs } from '../features/positions/UpdatePositionExternalIDs';
import { UploadPositionData } from '../features/positions/UploadPositionData';
import PageNotFound from '../pages/Error/PageNotFound';
import RecruitmentPolicyAttributes from '../features/recruitmentPolicy/RecruitmentPolicyAttributes';
import RecruitmentPolicy from '../features/recruitmentPolicy/RecruitmentPolicy';
import { ViewRecruitmentPolicy } from '../features/recruitmentPolicy/RecruitmentPolicy/ViewRecruitmentPolicy';
import CRPoliciesAdd from '../features/changeRequestPolicies/CRPoliciesAdd';
import MasterAttributes from '../features/masterAttributes';
import CompanySelect from '../features/companySelect';
import PreHQDashboard from '../features/PreHQ/dashboard';
import PreHQBulkUpload from '../features/PreHQ/positions/BulkUpload';
import PreHQCreatePositions from '../features/PreHQ/positions/CreatePosition';
import PreHQPositionDetails from '../features/PreHQ/positions/PositionDetails';
import PreHQPositions from '../features/PreHQ/positions/Position';
import PreHQChangeRequestPolicyRoles from '../features/PreHQ/changeRequestPolicyRoles';
//import PreHQApprovedRequests from '../features/PreHQ/approvedRequests';
import PreHQEditAndDraft from '../features/PreHQ/approvedRequests/EditAndDraft';
import PreHQCRPolicies from '../features/PreHQ/changeRequestPolicies';
import PreHQViewCRpolicies from '../features/PreHQ/changeRequestPolicies/ViewCRPolicies';
import PreHQCRPoliciesAdd from '../features/PreHQ/changeRequestPolicies/CRPoliciesAdd';
import PreHQModifyCRPolicies from '../features/PreHQ/changeRequestPolicies/ModifyCRPolicies';

const Navigation = () => {
    const currentUser = useSelector((state) => state.session.user);
    const isClientHqEnabled = currentUser?.isClientHqEnabled;

    return (
        <Routes>
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password/:token" element={<ResetPassword />} />
            <Route path="/" element={<App />}>
                {/* COMMON */}
                <Route
                    path="companySelect"
                    element={
                        <AuthorizedRouteWithLayout>
                            <CompanySelect />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="access-denied"
                    element={
                        <AuthorizedRouteWithLayout>
                            <AccessDenied />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route path="/*" element={<PageNotFound />} />
                {/* CLIENT HQ */}
                <Route path="client">
                    {/* DASHBOARD */}
                    <Route
                        path="dashboard"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_DASHBOARD']}
                            >
                                <PreHQDashboard />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    {/* POSITIONS */}
                    <Route
                        path="positions"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_LIST_POSITIONS']}
                            >
                                <PreHQPositions />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="positions/create-position"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_CREATE_POSITIONS']}
                            >
                                <PreHQCreatePositions />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="positions/bulk-upload"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_BULK_UPLOAD_POSITIONS']}
                            >
                                <PreHQBulkUpload />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="positions/bulk-edit"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_BULK_EDIT_POSITIONS']}
                            >
                                <PreHQBulkUpload />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="positions/details"
                        element={
                            <AuthorizedRouteWithLayout>
                                <PreHQPositionDetails requireClientHQEnabled={true} />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    {/* Approved Requests */}
                    <Route
                        path="approvedrequests/details"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_MOVE_TO_DRAFT_POSITIONS']}
                            >
                                <PreHQEditAndDraft />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    {/* CHANGE REQUEST POLICY */}
                    <Route
                        path="reviewrequest/reviewpolicies"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_LIST_REVIEW_POLICY']}
                            >
                                <PreHQCRPolicies />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="reviewrequest/reviewpolicies/view/:id"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_VIEW_REVIEW_POLICY']}
                            >
                                <PreHQViewCRpolicies />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="reviewrequest/reviewpolicies/add"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_CREATE_REVIEW_POLICY']}
                            >
                                <PreHQCRPoliciesAdd />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    <Route
                        path="reviewrequest/reviewpolicies/modify/:id"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_MODIFY_REVIEW_POLICY']}
                            >
                                <PreHQModifyCRPolicies />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                    {/* SETTINGS */}
                    <Route
                        path="settings/review-policy/roles"
                        element={
                            <AuthorizedRouteWithLayout
                                requireClientHQEnabled={true}
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_LIST_REVIEW_POLICY_ROLES']}
                            >
                                <PreHQChangeRequestPolicyRoles />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                </Route>
                {/* DASHBOARD */}
                <Route
                    path="dashboard"
                    element={
                        <AuthorizedRouteWithLayout
                            path="dashboard"
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_VIEW_DASHBOARD']}
                        >
                            <Dashboard />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* ACCOUNT */}
                <Route
                    path="account"
                    element={
                        <AuthorizedRouteWithLayout>
                            <Account />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="account/update/:id"
                    element={
                        <AuthorizedRouteWithLayout>
                            <Account />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* POSITIONS */}
                <Route
                    exact
                    path="positions"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_LIST_POSITIONS']}
                        >
                            <Positions />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="positions/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_VIEW_POSITION']}
                        >
                            <ViewPosition />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    exact
                    path="positions/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_POSITION']}
                        >
                            <AddPosition />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="positions/modify/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_POSITION']}
                        >
                            <ModifyPosition />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* <Route
                    path="positions/bulk-modify"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_BULK_UPDATE_POSITION_STATUS']}
                        >
                            <BulkModifyPositions />
                        </AuthorizedRouteWithLayout>
                    }
                /> */}
                {/* <Route
                    path="positions/bulk-modify/preview"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_IMPORT_POSITIONS_DATA']}
                        >
                            <PositionsUploadPreview />
                        </AuthorizedRouteWithLayout>
                    }
                /> */}
                {/* RECRUITMENT POLICY */}
                <Route
                    exact
                    path="recruitmentPolicies"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_FETCH_RECRUITMENT_POLICY']}
                        >
                            <RecruitmentPolicy />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    exact
                    path="recruitmentPolicies/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_RECRUITMENT_POLICY_DETAILS']}
                        >
                            <ViewRecruitmentPolicy />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* CHANGE REQUEST POLICY */}
                <Route
                    path="changerequest/crpolicies"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_LIST_CHANGE_REQUEST_POLICIES']}
                        >
                            <CRPolicies />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changerequest/crpolicies/view/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_VIEW_CHANGE_REQUEST_POLICY']}
                        >
                            <ViewCRpolicies />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changerequest/crpolicies/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST_POLICY']}
                        >
                            <CRPoliciesAdd />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changerequest/crpolicies/modify/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_CHANGE_REQUEST_POLICY']}
                        >
                            <ModifyCRPolicies />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changerequest/crpolicies/mappedpositions/view/:crPolicyId"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={[
                                'ROLE_BASE_PERMISSION_RECRUIT_INDEX_VIEW_POSITIONS_MAPPED_TO_CHANGE_REQUEST_POLICY',
                            ]}
                        >
                            <ViewUpdateMappedPositions />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changerequest/crpolicies/mappedpositions/update/:crPolicyId"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={[
                                'ROLE_BASE_PERMISSION_RECRUIT_INDEX_BULK_UPDATE_POSITION_CHANGE_REQUEST_POLICY_MAPPINGS',
                            ]}
                        >
                            <ViewUpdateMappedPositions />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* CHANGE REQUEST */}
                <Route
                    path="changerequests"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_LIST_CHANGE_REQUESTS']}
                        >
                            <ChangeRequests />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changeRequests/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_VIEW_CHANGE_REQUEST']}
                        >
                            <ViewChangeRequests />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changeRequests/create/:positionId"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST']}
                        >
                            <ModifyChangeRequest />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changeRequests/:id/modify/:positionId"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_CHANGE_REQUEST']}
                        >
                            <ModifyChangeRequest />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changeRequests/bulk/create"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST']}
                        >
                            <ModifyChangeRequest />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="changeRequests/bulk/modify"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_CHANGE_REQUEST']}
                        >
                            <ModifyChangeRequest />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* COMPANY */}
                <Route
                    exact
                    path="company"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_LIST_COMPANIES']}
                        >
                            <Company />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="company/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_VIEW_COMPANY']}
                        >
                            <ViewCompany />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="company/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_CREATE_COMPANY']}
                        >
                            <AddCompany />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="company/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_CREATE_COMPANY']}
                        >
                            <AddCompany />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="company/:companyId/config"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_COMPANY_CONFIG_UPDATE']}>
                            <CompanyConfig />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* USERS */}
                <Route
                    path="users"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_EMPLOYEE_INDEX_LIST_SECUSER']}
                        >
                            <Users />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="users/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_EMPLOYEE_INDEX_CREATE_SECUSER']}
                        >
                            <AddEditUser redirectRoute={'/users'} />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="users/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_EMPLOYEE_INDEX_EDIT_SECUSER']}
                        >
                            <AddEditUser redirectRoute={'/users'} />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="users/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_EMPLOYEE_INDEX_VIEW_SECUSER']}
                        >
                            <ViewUser />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="users/bulk-upload"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_BULK_UPLOAD_USERS_SECUSER']}
                        >
                            <BulkUploadUsers />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* MASTER CUSTOM ATTRIBUTES */}
                <Route
                    path="masterCustomAttributes"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_VIEW_MASTER_CUSTOM_ATTRIBUTE']}
                        >
                            <MasterAttributes />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* SETTINGS */}
                {/* ROLES */}
                <Route
                    path="settings/roles"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_SECROLE_LIST']}>
                            <Roles />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/roles/:id"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_SECROLE_VIEW']}>
                            <ViewRoles />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/roles/add"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_SECROLE_CREATE']}>
                            <AddEditRoles />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/roles/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_SECROLE_UPDATE']}>
                            <AddEditRoles />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* LOCATIONS */}
                <Route
                    path="settings/locations"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_LIST_LOCATIONS']}>
                            <Locations />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/locations/:id"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_VIEW_LOCATION']}>
                            <ViewLocation />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/locations/add"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_CREATE_LOCATION']}>
                            <AddEditLocation />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/locations/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_UPDATE_LOCATION']}>
                            <AddEditLocation />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* DESIGNATIONS */}
                <Route
                    path="settings/designations"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_LIST_DESIGNATIONS']}
                        >
                            <Designations />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/designations/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_VIEW_DESIGNATION']}
                        >
                            <ViewDesignations />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/designations/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_CREATE_DESIGNATION']}
                        >
                            <AddEditDesignation />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/designations/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_UPDATE_DESIGNATION']}
                        >
                            <AddEditDesignation />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* DEPARTMENTS */}
                <Route
                    path="settings/departments"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_LIST_DEPARTMENTS']}
                        >
                            <Departments />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/departments/:id"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_VIEW_DEPARTMENT']}>
                            <ViewDepartment />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/departments/add"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_CREATE_DEPARTMENT']}
                        >
                            <AddEditDepartment />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/departments/edit/:id"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_UPDATE_DEPARTMENT']}
                        >
                            <AddEditDepartment />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* POSITIONS */}
                <Route
                    path="settings/modify-position-external-ids"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_EXTERNAL_ENTITY_ID']}
                        >
                            <UpdatePositionExternalIDs />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {!isClientHqEnabled && (
                    <Route
                        path="settings/upload-position-data"
                        element={
                            <AuthorizedRouteWithLayout
                                requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_IMPORT_POSITIONS_DATA']}
                            >
                                <UploadPositionData />
                            </AuthorizedRouteWithLayout>
                        }
                    />
                )}
                {/* CHANGE POLICY ROLES */}
                <Route
                    path="settings/change-policy/roles"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_ROLES_FOR_APPROVERS']}
                        >
                            <ChangeRequestPolicyRoles />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* DEMAND ATTRIBUTES */}
                <Route
                    path="settings/demand-attributes"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={[
                                'ROLE_BASE_PERMISSION_INDEX_LIST_META_OBJECT_DEFN',
                                'ROLE_BASE_PERMISSION_INDEX_VIEW_META_OBJECT_DEFN',
                            ]}
                        >
                            <AddCustomAttributes />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* RECRUITMENT POLICY */}
                <Route
                    path="settings/recruitment-policy/attributes"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={[
                                'ROLE_BASE_PERMISSION_RECRUIT_INDEX_PRE_CREATE_RULE_SETTINGS_FOR_RECRUITMENT_POLICY',
                            ]}
                        >
                            <RecruitmentPolicyAttributes />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* INTEGRATIONS */}
                <Route
                    path="settings/update-integration"
                    element={
                        <AuthorizedRouteWithLayout requiredPermissions={['ROLE_BASE_PERMISSION_COMPANY_CONFIG_UPDATE']}>
                            <UpdateIntegration />
                        </AuthorizedRouteWithLayout>
                    }
                />
                {/* PERMISSIONS */}
                <Route
                    path="settings/request-maps"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_SUPER_ADMINISTRATOR', 'ROLE_BASE_PERMISSION_SYSCONFIG_UPDATE']}
                        >
                            <RequestMaps />
                        </AuthorizedRouteWithLayout>
                    }
                />
                <Route
                    path="settings/system-permissions"
                    element={
                        <AuthorizedRouteWithLayout
                            requiredPermissions={['ROLE_SUPER_ADMINISTRATOR', 'ROLE_BASE_PERMISSION_SYSCONFIG_UPDATE']}
                        >
                            <SystemPermission />
                        </AuthorizedRouteWithLayout>
                    }
                />
            </Route>
        </Routes>
    );
};

export default Navigation;
